/**
 * Medidas de la tarjeta del directorio, en un solo lugar.
 *
 * POR QUÉ ESTE MÓDULO EXISTE. La grilla está virtualizada: `useVirtualizer`
 * necesita saber cuánto mide una fila ANTES de montarla. Mientras el tamaño de
 * la tarjeta se calculaba dentro del JSX y la altura estimada se escribía a mano
 * en `estimateSize`, eran dos fuentes de verdad para el mismo número: tocar un
 * padding y olvidar la estimación desalineaba el scroll, y el error no se veía
 * hasta scrollear lejos. Acá se calcula UNA vez y la consumen los dos.
 *
 * ESCALA HONESTA. El control anterior mostraba 85–200 % pero aplicaba
 * `1 + (zoom - 100) / 260`: en el máximo agrandaba un 38 %, no el doble, y el
 * ancho corría por una fórmula distinta (`250 + (zoom - 100) * 0.85`), así que
 * la tarjeta cambiaba de proporción mientras se movía el control. Acá el
 * porcentaje es literal: al 200 % la tarjeta mide el doble.
 *
 * EL TEXTO CRECE MENOS QUE LA CAJA. La caja escala 1:1 con el zoom; la
 * tipografía va amortiguada (`scale ** 0.75`). Al 200 % eso da una tarjeta del
 * doble de tamaño con texto 1,7 veces más grande — el aire sobrante es
 * deliberado: es el lugar donde entran los campos que la tarjeta va a mostrar
 * cuando haya datos que mostrar. Sin amortiguar, el zoom alto solo produce
 * letras enormes.
 */

export const ZOOM_MIN = 70;
export const ZOOM_MAX = 200;
export const ZOOM_STEP = 5;
/** Tamaño de referencia. El default anterior (115 %) no era una decisión: era el
 *  tamaño base más un 6 %, heredado de la escala vieja. */
export const ZOOM_DEFAULT = 100;

/** Cuánto se amortigua la tipografía respecto de la caja. 1 = crece igual. */
const TEXT_DAMPING = 0.75;

/** Medidas al 100 %. Equivalen al 90 % de la escala anterior: la tarjeta parte
 *  más compacta y el recorrido hacia arriba queda para quien lo necesite. */
const BASE = {
	// 236 y no 242, y los 6 px de diferencia no son cosméticos: mandan sobre
	// cuántas columnas entran. Con el gap de 16, a 1320 px de contenido entran 5
	// tarjetas y a partir de 1298 caen a 4. Bajar el ancho del contenido en
	// pantallas chicas —para que respire— cruzaba ese umbral; bajando también la
	// tarjeta, el aire se gana sin pagar una columna.
	width: 236,
	padding: 14.4,
	photo: 69,
	/** Separación entre la foto y el bloque de texto. */
	gap: 9.6,
	/** Separación entre las líneas del bloque de texto. */
	textGap: 4.3,
	nameFont: 14.4,
	badgeFont: 10.6,
	unitFont: 12.5,
	countFont: 10.4,
} as const;

const NAME_LINE_HEIGHT = 1.3;
const UNIT_LINE_HEIGHT = 1.375;
/** Líneas reservadas para la unidad. Se reservan SIEMPRE, entren una o dos:
 *  una tarjeta que cambia de alto según el largo del nombre haría variar el alto
 *  de la fila entera, y `estimateSize` dejaría de poder predecirlo. */
const UNIT_LINES = 2;
/** Alto propio del badge por encima de su tipografía: `py-0.5` (2 px arriba y
 *  abajo) más 1 px de borde por lado. */
const BADGE_CHROME = 6;

export interface DirectoryCardMetrics {
	/** Ancho mínimo de tarjeta — alimenta el `minmax()` del grid. */
	width: number;
	padding: number;
	photo: number;
	photoIcon: number;
	gap: number;
	textGap: number;
	nameFont: number;
	badgeFont: number;
	unitFont: number;
	countFont: number;
	/** Alto reservado para la línea del badge de estamento. */
	badgeRow: number;
	/** Alto reservado para el nombre de la unidad. */
	unitBlock: number;
	/** Alto total de la tarjeta. Es también lo que mide una fila de la grilla. */
	height: number;
}

const round = (v: number) => Math.round(v * 100) / 100;

export function getDirectoryCardMetrics(zoom: number): DirectoryCardMetrics {
	const scale = zoom / 100;
	const textScale = scale ** TEXT_DAMPING;

	const padding = BASE.padding * scale;
	const photo = BASE.photo * scale;
	const gap = BASE.gap * scale;
	const textGap = BASE.textGap * scale;
	const nameFont = BASE.nameFont * textScale;
	const badgeFont = BASE.badgeFont * textScale;
	const unitFont = BASE.unitFont * textScale;

	const nameLine = nameFont * NAME_LINE_HEIGHT;
	const badgeRow = badgeFont + BADGE_CHROME * scale;
	const unitBlock = unitFont * UNIT_LINE_HEIGHT * UNIT_LINES;

	// El mismo orden en que se apilan los elementos dentro de la tarjeta. Si el
	// JSX cambia de estructura, esta suma tiene que cambiar con él.
	const height =
		padding * 2 + photo + gap + nameLine + textGap + badgeRow + textGap + unitBlock;

	return {
		width: Math.round(BASE.width * scale),
		padding: round(padding),
		photo: round(photo),
		photoIcon: round(photo * 0.5),
		gap: round(gap),
		textGap: round(textGap),
		nameFont: round(nameFont),
		badgeFont: round(badgeFont),
		unitFont: round(unitFont),
		countFont: round(BASE.countFont * textScale),
		badgeRow: round(badgeRow),
		unitBlock: round(unitBlock),
		height: Math.round(height),
	};
}
